'use client';

import classNames from 'classnames';
import { InputHTMLAttributes, useState } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { twMerge } from 'tailwind-merge';
import { Button } from '../../../Button';
import { IsVisible } from '../../../utils';
import { FieldBase, IFieldBaseProps } from '../Base';

export interface IPasswordFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  containerProps?: {
    className?: string;
    rest?: Omit<IFieldBaseProps, 'className'>;
  };
  showPasswordButton?: boolean;
}

export function PasswordField({
  className,
  containerProps,
  disabled = false,
  name,
  readOnly = false,
  showPasswordButton = true,
  ...rest
}: IPasswordFieldProps): JSX.Element {
  const [passwordType, setPasswordType] = useState<'password' | 'text'>(
    'password',
  );

  const handleChangePasswordType = (type: string) => {
    if (type === 'password') {
      setPasswordType('text');
    } else {
      setPasswordType('password');
    }
  };

  return (
    <FieldBase
      className={twMerge('grid-cols-[1fr_auto]', containerProps?.className)}
      {...containerProps?.rest}
    >
      <input
        className={classNames(
          twMerge(
            'bg-transparent flex flex-1 h-full min-w-fit text-sm text-gray-800 data-[placeholder]:text-sm focus-visible:outline-0',
            className,
          ),
          {
            'disabled:cursor-not-allowed disabled:text-gray-700 read-only:cursor-not-allowed read-only:text-gray-700':
              disabled || readOnly,
          },
        )}
        disabled={disabled}
        name={name}
        readOnly={readOnly}
        type={passwordType}
        {...rest}
      />
      <IsVisible when={showPasswordButton}>
        <Button
          className="text-gray-600 hover:opacity-70 h-full"
          design="icon-only"
          onClick={() => handleChangePasswordType(passwordType)}
          type="button"
        >
          <IsVisible when={passwordType === 'text'}>
            <FiEyeOff size={18} />
          </IsVisible>
          <IsVisible when={passwordType === 'password'}>
            <FiEye size={18} />
          </IsVisible>
        </Button>
      </IsVisible>
    </FieldBase>
  );
}
